// Module: UrlInputBrick
// Role: Self-contained URL input brick for flow engine.
//       Paste URL -> submit as url-kind artefact (no presign, no file upload).
// Uses: api/flow-client
// Used by: brick-registry.ts (kind='url')
//
// Pattern: PublishLinks LinkInput UX adapted for single-URL submit.
// ADR-023: no platform literals.
// L-118: hints "screenshot also needed" when checkpoint has both url + screenshot reqs.

import { useState, useCallback } from 'react';
import { useSubmitCheckpoint } from '../../../api/flow-client';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { Ic } from '../../../components/atoms/Ic';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import type {
  Checkpoint,
  CheckpointAttempt,
  SlotFlowRun,
} from '../../../api/flow-types';

const SUCCESS_SOFT_BG = '#EFF7E9';

export interface UrlInputBrickProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

export function UrlInputBrick({
  checkpoint,
  flowRun,
  checkpoints,
}: UrlInputBrickProps) {
  const [url, setUrl] = useState('');
  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  const submitCheckpoint = useSubmitCheckpoint(
    flowRun.slot_id,
    checkpoint.id,
  );

  const parentCount = checkpoints.filter(
    (c) => c.parent_checkpoint_id === null,
  ).length;

  const isValidUrl =
    url.startsWith('http://') || url.startsWith('https://');

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      setUrl(text.trim());
    } catch {
      // clipboard denied -- user can type manually
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!isValidUrl) {
      setHintMessage(
        'Вставь ссылку, начинающуюся с http:// или https://',
      );
      return;
    }

    setHintMessage(null);

    try {
      // Submit as url-kind artefact (uses multi-artefact contract from Task 1)
      await submitCheckpoint.mutateAsync({
        artefact_path: null,
        artefacts: [{ kind: 'url', value: url }],
      });
      setIsDone(true);
    } catch {
      setHintMessage('Ошибка отправки. Попробуй ещё раз.');
    }
  }, [url, isValidUrl, submitCheckpoint]);

  return (
    <Frame>
      <MiniHeader title={checkpoint.display_name ?? flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip current={checkpoint.position + 1} total={parentCount} showCaption />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Вставь ссылку на публикацию
          </div>
          <div
            style={{
              font: T.footnote,
              color: S.secondaryLabel,
              marginTop: 4,
            }}
          >
            {checkpoint.instructions_text ??
              'Скопируй ссылку из приложения'}
          </div>
        </div>

        <InstructionCard p={12}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                disabled={isDone}
                style={{
                  flex: 1,
                  background: isDone ? SUCCESS_SOFT_BG : P.card,
                  border: `1px solid ${isDone ? P.success : P.line}`,
                  borderRadius: 12,
                  padding: '10px 12px',
                  fontFamily: T.fontBody,
                  fontSize: 15,
                  color: P.ink,
                  outline: 'none',
                  WebkitAppearance: 'none',
                  boxSizing: 'border-box',
                }}
                data-testid="url-input"
              />
              {!isDone && (
                <button
                  onClick={handlePaste}
                  aria-label="Вставить ссылку"
                  style={{
                    padding: '0 14px',
                    background: P.cream,
                    border: '1px solid rgba(192,81,58,0.18)',
                    borderRadius: 12,
                    fontFamily: T.fontBody,
                    fontSize: 13,
                    fontWeight: 600,
                    color: P.terraDark,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                  }}
                >
                  Вставить
                </button>
              )}
            </div>
            {isDone && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Ic name="check" size={14} color={P.success} />
                <span style={{ font: T.caption, color: P.success }}>
                  Ссылка отправлена
                </span>
              </div>
            )}
          </div>
        </InstructionCard>

        {hintMessage && <HintBar>{hintMessage}</HintBar>}
      </div>

      <WizardCtaBar
        label={isDone ? 'Готово' : 'Отправить ссылку'}
        onClick={handleSubmit}
        disabled={isDone || !isValidUrl || submitCheckpoint.isPending}
      />
    </Frame>
  );
}
